// Dashboard PBJ (Non-Penyedia, feature pbj_dashboard): the user's PBJ work
// for the CURRENT role (Mode menu).
//   header    plain spacing   h1, the role in the lead, Paket PBJ baru
//   figures   four tiles      Paket PBJ, Aktivitas diselesaikan, Alur PBJ
//                             (diagrams), Template dan keluaran (the role's
//                             templates and the outputs Template > Keluaran dari
//                             alur PBJ lists, the same count)
//   projects  region, cards   one card per PBJ project (pbj/ProjectCard): name,
//                             unit kerja and jenis, route, current stage,
//                             progress % with done/total; each opens its
//                             project; Lihat semua opens the Project page
//   pending   region, rows    open activities of each project's current stage
//                             that the role acts in; each row opens its project
//   quick     region, cards   Alur PBJ and Pusat Regulasi (when their feature is on)
// No project yet: the projects region holds one empty state with Paket PBJ
// baru (the Buat project dialog); the figures and quick access stay.
import * as API from "../api";
import type { PbjDashboard } from "../api";
import { useUser } from "../auth";
import { formatCount } from "../lib/format";
import { ROLE_LABEL } from "../lib/labels";
import { PBJ_STAGE_LABEL } from "../pbj/labels";
import { PbjProjectCard } from "../pbj/ProjectCard";
import { PBJ_PROJECTS_PATH } from "../shell/nav";
import { useFeatures } from "../shell/features";
import { projectPath, useProjects } from "../shell/Projects";
import { EmptyState, Notice, PageHeader, SectionHead, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph, type GlyphName } from "../ui/icons";

export function DashboardPbjScreen() {
  useTitle("Dashboard PBJ");
  const user = useUser();
  const projects = useProjects();
  const features = useFeatures();
  const templatesOn = features.has("templates");
  const dash = useAsync(() => API.getPbjDashboard(), [user.role]);
  // The template count is a figure only: a failure leaves it out, never the page.
  const templates = useAsync(() => (templatesOn ? API.getTemplates().then((t) => t.templates.length).catch(() => null) : Promise.resolve(null)), [templatesOn, user.role]);
  const d = dash.data;
  const create = projects ? (
    <button type="button" className="btn btn-primary" onClick={projects.openCreate}>
      <Glyph name="add" size={18} />
      <span>Paket PBJ baru</span>
    </button>
  ) : undefined;

  return (
    <>
      <PageHeader divided title="Dashboard PBJ" lead={`Ringkasan paket PBJ Anda dalam mode ${ROLE_LABEL[user.role]}.`} actions={create} />
      {dash.loading && !d ? (
        <SkeletonRows count={4} label="Memuat ringkasan" />
      ) : dash.error ? (
        <Notice tone="danger" title="Ringkasan belum termuat" action={<button type="button" className="btn btn-secondary" onClick={dash.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(dash.error)}</p>
        </Notice>
      ) : d ? (
        <>
          <dl className="pbj-figures">
            <div className="pbj-figure">
              <dt>Paket PBJ</dt>
              <dd className="num">{formatCount(d.projects)}</dd>
            </div>
            <div className="pbj-figure">
              <dt>Aktivitas diselesaikan</dt>
              <dd className="num">
                {formatCount(d.activitiesDone)} <span className="pbj-figure-of">dari {formatCount(d.activitiesTotal)}</span>
              </dd>
            </div>
            <div className="pbj-figure">
              <dt>Alur PBJ</dt>
              <dd className="num">
                {formatCount(d.diagrams)} <span className="pbj-figure-of">diagram</span>
              </dd>
            </div>
            <div className="pbj-figure">
              <dt>Template dan keluaran</dt>
              <dd className="num">
                {formatCount((templates.data ?? 0) + d.outputs)}{" "}
                <span className="pbj-figure-of">{templates.data != null ? `${formatCount(templates.data)} template, ${formatCount(d.outputs)} keluaran` : `${formatCount(d.outputs)} keluaran aktivitas`}</span>
              </dd>
            </div>
          </dl>

          <section className="region" aria-labelledby="pbj-projects">
            <SectionHead
              id="pbj-projects"
              title="Paket PBJ"
              lead="Kelola tiap paket melalui tahap PBJ, dari rencana kerja dan anggaran sampai serah terima."
              action={
                d.items.length > 0 ? (
                  <a className="btn btn-secondary" href={PBJ_PROJECTS_PATH}>
                    <span>Lihat semua</span>
                    <Glyph name="chevronRight" size={18} />
                  </a>
                ) : undefined
              }
            />
            {d.items.length === 0 ? (
              <EmptyState icon="building" title="Belum ada paket PBJ" body="Buat paket PBJ dari Project baru untuk mengikuti tahap pengadaan dan aktivitasnya." action={create} />
            ) : (
              <ul className="pbj-cards" aria-label="Paket PBJ">
                {d.items.map((p) => (
                  <li key={p.id}>
                    <PbjProjectCard project={p} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {d.items.length > 0 ? <Pending d={d} roleLabel={ROLE_LABEL[user.role]} /> : null}

          <QuickAccess flowOn={features.has("pbj_flow")} regulationsOn={features.has("regulations")} />
        </>
      ) : null}
    </>
  );
}

function Pending({ d, roleLabel }: { d: PbjDashboard; roleLabel: string }) {
  return (
    <section className="region" aria-labelledby="pbj-pending">
      <SectionHead id="pbj-pending" title="Aktivitas menunggu peran Anda" lead={`Aktivitas tahap berjalan yang pelakunya ${roleLabel} dan belum dicentang.`} />
      {d.pending.length === 0 ? (
        <EmptyState icon="tickCircle" title="Tidak ada yang menunggu" body={`Semua aktivitas ${roleLabel} di tahap berjalan sudah dicentang.`} />
      ) : (
        <ul className="rows">
          {d.pending.map((p) => (
            <li key={`${p.projectId}-${p.nodeId}`}>
              <a className="row row-link" href={projectPath(p.projectId)}>
                <span className="row-main">
                  <span className="row-title">{p.activity}</span>
                  <span className="row-meta">
                    {p.projectName}, tahap {PBJ_STAGE_LABEL[p.stage]}
                    {p.output ? `. Output: ${p.output}` : ""}
                  </span>
                </span>
                <span className="row-chevron" aria-hidden="true">
                  <Glyph name="chevronRight" size={18} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
      {d.pendingTotal > d.pending.length ? (
        <p className="pbj-next">
          {formatCount(d.pending.length)} dari {formatCount(d.pendingTotal)} aktivitas ditampilkan.
        </p>
      ) : null}
    </section>
  );
}

const QUICK: { key: "flow" | "regulations"; href: string; icon: GlyphName; title: string; body: string; action: string }[] = [
  { key: "flow", href: "/alur-pbj", icon: "layers", title: "Jelajahi semua alur PBJ", body: "Tahap utama, metode pemilihan Penyedia, Swakelola Tipe I sampai IV, dan titik keputusannya.", action: "Buka Alur PBJ" },
  { key: "regulations", href: "/regulasi", icon: "judge", title: "Regulasi PBJ terbaru", body: "Dasar hukum pengadaan dengan tautan JDIH untuk memeriksa naskah resminya.", action: "Buka Pusat Regulasi" },
];

function QuickAccess({ flowOn, regulationsOn }: { flowOn: boolean; regulationsOn: boolean }) {
  const items = QUICK.filter((q) => (q.key === "flow" ? flowOn : regulationsOn));
  if (items.length === 0) return null;
  return (
    <section className="region" aria-labelledby="pbj-quick">
      <SectionHead id="pbj-quick" title="Akses cepat" />
      <ul className="pbj-cards pbj-quick" aria-label="Akses cepat">
        {items.map((q) => (
          <li key={q.key}>
            <a className="pbj-card" href={q.href}>
              <span className="pbj-card-top">
                <Glyph name={q.icon} size={18} />
              </span>
              <span className="pbj-card-title">{q.title}</span>
              <span className="pbj-card-meta">{q.body}</span>
              <span className="pbj-card-open">
                {q.action} <Glyph name="chevronRight" size={16} />
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
